// The live DataSource. Every method calls the real API through services/http.ts
// (Firebase token attach, one 401 refresh-retry, ns path encoding).
//
// Methods still listed in STATIC_BACKED have no endpoint and delegate to
// staticSource so the console renders complete — see ROADMAP.md. That list is
// now down to the parts of billing that need a payment provider.

import { notifications } from "@mantine/notifications";
import { ApiError, nsPath, request } from "../services/http";
import { applyWorkspacePrefix, stripWorkspacePrefix } from "../lib/namespace";
import type {
  Analytics, ApiKey, Billing, CompactionEntry, ConsoleSettings, DocumentRecord,
  EmbedderProfile, HealthTile, Job, Member, MultiQueryOutcome, Namespace,
  NamespaceDraft, Operation, QueryHit, QueryOutcome, Repo, TrashEntry, UpsertAck,
  Workspace, WorkspaceSummary,
} from "../types/domain";
import type {
  DataSource, DocumentDraft, InviteDraft, KeyDraft, MultiQueryInput, QueryInput,
  RepoDraft, WorkspaceDraft,
} from "./types";
import { STATIC_BACKED } from "./types";
import { staticSource } from "./staticSource";
import type {
  ListDocumentsResponse, MultiQueryResponse, QueryResponse, Stats,
  UpsertResponse, Workspace as ApiWorkspace,
} from "../types/api";

const STATIC_FALLBACK = import.meta.env.VITE_VF_STATIC_FALLBACK !== "false";

/**
 * Static-backed methods that MUTATE. Reading fixtures so a screen renders is one
 * thing; letting a write look like it persisted is another — these announce
 * themselves so nobody believes the change survived the page.
 *
 * Empty now that every console mutation has a real endpoint. Kept (rather than
 * deleted) because the guard is what makes adding a future fixture-backed write
 * safe by default.
 */
const STATIC_WRITES = new Set<keyof DataSource>([]);

function announceFakeWrite(name: string): void {
  notifications.show({
    color: "yellow",
    title: "not persisted",
    message: `${name} has no endpoint yet — the change is local only (see ROADMAP.md)`,
  });
}

// ---------------------------------------------------------------- mapping ---

const DASH = "—";

/** statsResponseCore in internal/server/core_handlers.go. */
interface ApiStats extends Stats {
  display_name?: string;
  last_write_at?: string;
  status?: string;
  kind?: string;
}

interface ApiNamespaceEntry {
  namespace: string;
  vector_count?: number;
  stats?: ApiStats;
}

/**
 * The server now labels the retrieval mode ("vector" | "bm25" | "hybrid"). The
 * console's older vocabulary is kept so the screens read the same; only the
 * source of truth moved.
 */
function kindFor(full: string, stats: Partial<ApiStats>): string {
  if (stats.kind === "bm25") return "BM25-only";
  if (full.includes(":")) return "Code search";
  return "Knowledge base";
}

/**
 * The server's derived namespace status, mapped onto the console's status
 * vocabulary: "pending" compaction shows as queued work, and a namespace with
 * no base and no deltas is idle rather than ready — "ready" on something that
 * would return nothing reads as a working index.
 */
function statusFor(raw: string | undefined): Namespace["status"] {
  switch (raw) {
    case "pending":
      return "queued";
    case "empty":
      return "idle";
    default:
      return "ready";
  }
}

function namespaceFromStats(full: string, workspaceId: string, stats: Partial<ApiStats>): Namespace {
  return {
    id: full,
    label: stats.display_name || stripWorkspacePrefix(full, workspaceId) || full,
    kind: kindFor(full, stats),
    metric: "cosine",
    dims: stats.dimensions ?? 0,
    profile: stats.embedder_profile ?? DASH,
    vectors: stats.vector_count ?? 0,
    deltas: stats.delta_segments ?? 0,
    tombstones: stats.delta_tombstones ?? 0,
    sizeBytes: stats.size_bytes ?? 0,
    updated: stats.last_write_at ? shortDate(stats.last_write_at) : DASH,
    // Traffic columns are joined in from /v1/analytics by listNamespaces; a
    // namespace fetched on its own has none, which reads as "no traffic
    // recorded" rather than a fabricated number.
    searches: 0,
    p95: 0,
    cacheHit: 0,
    errors: 0,
    bm25: stats.bm25_enabled ?? false,
    status: statusFor(stats.status),
    baseVer: stats.base_version ?? 0,
  };
}

/** "2026-07-26T10:00:00Z" → "2026-07-26". */
function shortDate(iso: string): string {
  return iso.slice(0, 10);
}

/** 4210 → "1h 10m", 42 → "42s". Server-computed seconds, formatted here. */
function humanAge(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return DASH;
  if (seconds < 60) return `${Math.floor(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function bytes(n: number): string {
  if (n <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  const v = n / 1024 ** i;
  return `${v >= 10 || i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}

function compact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function hitsFrom(results: QueryResponse["results"]): QueryHit[] {
  return (results ?? []).map((r) => ({
    id: r.id,
    score: r.score,
    content: r.content ?? "",
    metadata: (r.metadata as Record<string, unknown>) ?? {},
    namespace: r.namespace,
  }));
}

// ------------------------------------------------------------- wire shapes --

/** operationView in internal/server/console_handlers.go. */
interface RawOperation {
  id: string;
  namespace: string;
  kind?: string;
  docs?: number;
  status: string;
  delta_sequence?: number;
  pieces_done?: number;
  error?: string;
  created_at?: string;
  updated_at?: string;
  age_seconds?: number;
}

function mapOperation(o: RawOperation): Operation {
  return {
    id: o.id,
    namespace: o.namespace,
    kind: (o.kind ?? "upsert") as Operation["kind"],
    docs: o.docs ?? 0,
    status: (o.status ?? "queued") as Operation["status"],
    age: humanAge(o.age_seconds ?? -1),
    attempt: 1,
    seq: o.delta_sequence ?? DASH,
    pieces: Math.max(1, o.pieces_done ?? 1),
    error: o.error,
  };
}

/** analyticsResponse. */
interface RawAnalytics {
  hours: number;
  searches_total: number;
  p50_ms: number;
  p95_ms: number;
  error_rate: number;
  series: { searches: number[]; p95_ms: number[] };
  cache: { l1: number; l2: number; miss: number };
  namespaces: {
    namespace: string; searches: number; p50_ms: number; p95_ms: number;
    cache_hit: number; errors: number;
  }[];
  shared: boolean;
}

/** meterView / billingResponse. */
interface RawBilling {
  workspace_id: string;
  plan: string;
  payment_provider: string;
  metering_shared: boolean;
  estimate_micro_dollars: number;
  meters: {
    key: string; label: string; used: number; unit: string;
    rate?: string; amount_micro_dollars?: number; estimated?: boolean;
  }[];
  quotas?: { key: string; label: string; used: number; limit: number }[];
  subscription?: {
    plan: string; status: string; current_period_end?: string;
    card_brand?: string; card_last4?: string; billing_email?: string;
  } | null;
  invoices: {
    id: string; amount_due_cents: number; status: string;
    period_start: string; period_end: string; hosted_url?: string;
  }[];
}

/** settingsResponse. */
interface RawSettings {
  workspace_id: string;
  name: string;
  plan: string;
  default_embedder_profile?: string;
  role?: string;
}

/** DeploymentInfo — read-only process configuration. */
interface RawConfig {
  ingest_backend: string;
  auth_chain: string[];
  auth_enabled: boolean;
  rate_limit?: string;
  code_search: boolean;
  cors_allowlist: string[];
  default_embedder_profile?: string;
}

/** HealthResponse — the public liveness probe. */
interface RawHealth {
  status: string;
  store: string;
  cache: string;
  namespaces_cached: number;
  uptime_seconds: number;
}

/** HealthDetail — GET /v1/health/detail, authenticated. */
interface RawHealthDetail {
  instance: string;
  ingest_backend?: string;
  ingest_owner: boolean;
  cache_shared: boolean;
  cache_hit_rate?: number;
  embedder?: { profile?: string; p95_ms?: number };
  metering_shared: boolean;
}

// ---------------------------------------------------------------- source ----

const live: DataSource = {
  kind: "http",

  // ---- workspaces -------------------------------------------------------
  listWorkspaces: async (): Promise<Workspace[]> => {
    const res = await request<{ workspaces: ApiWorkspace[] | null }>("/v1/workspaces");
    return (res.workspaces ?? []).map((w) => ({ id: w.id, name: w.name, role: w.role }));
  },

  createWorkspace: async (draft: WorkspaceDraft): Promise<Workspace> => {
    const w = await request<ApiWorkspace>("/v1/workspaces", {
      method: "POST",
      body: JSON.stringify({ name: draft.name }),
    });
    return { id: w.id, name: w.name, role: w.role };
  },

  listMembers: async (workspaceId: string): Promise<Member[]> => {
    const res = await request<{ members: RawMember[] | null }>(
      `/v1/workspaces/${encodeURIComponent(workspaceId)}/members`,
    );
    return (res.members ?? []).map(mapMember);
  },

  inviteMember: async (workspaceId: string, draft: InviteDraft): Promise<Member> => {
    const m = await request<RawMember>(
      `/v1/workspaces/${encodeURIComponent(workspaceId)}/members`,
      { method: "POST", body: JSON.stringify({ email: draft.email, role: draft.role }) },
    );
    return mapMember(m);
  },

  // ---- namespaces -------------------------------------------------------
  listNamespaces: async (workspaceId: string): Promise<Namespace[]> => {
    // ?include=stats folds what used to be an N+1 of per-namespace /stats calls
    // into one response.
    const res = await request<{ namespaces: ApiNamespaceEntry[] | null }>(
      "/v1/namespaces?include=stats",
    );
    const mine = (res.namespaces ?? []).filter(
      (n) => stripWorkspacePrefix(n.namespace, workspaceId) !== null,
    );
    const rows = mine.map((entry) =>
      namespaceFromStats(entry.namespace, workspaceId, entry.stats ?? {
        vector_count: entry.vector_count,
      }),
    );
    // Traffic comes from metering, not from the manifest, so it is a separate
    // read joined on the namespace key. A metering failure leaves the columns
    // at zero rather than failing the whole list.
    try {
      const a = await request<RawAnalytics>("/v1/analytics");
      const byNS = new Map(a.namespaces.map((n) => [n.namespace, n]));
      for (const row of rows) {
        const t = byNS.get(row.id);
        if (!t) continue;
        row.searches = t.searches;
        row.p95 = t.p95_ms;
        row.cacheHit = t.cache_hit;
        row.errors = t.errors;
      }
    } catch {
      /* metering unavailable — the structural columns still render */
    }
    return rows;
  },

  getNamespace: async (id: string): Promise<Namespace | null> => {
    try {
      const stats = await request<ApiStats>(nsPath(id, "/stats"));
      const workspaceId = id.split("/")[0] ?? "";
      return namespaceFromStats(id, workspaceId, stats);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return null;
      throw e;
    }
  },

  createNamespace: async (workspaceId: string, draft: NamespaceDraft): Promise<Namespace> => {
    const full = applyWorkspacePrefix(draft.name.trim(), workspaceId);
    await request(nsPath(full), {
      method: "POST",
      body: JSON.stringify({
        dimensions: draft.dims,
        distance_metric: draft.metric,
        embedder_profile: draft.profile,
        ...(draft.bm25 ? { bm25_fields: [{ field: "content", boost: 1 }] } : {}),
      }),
    });
    return {
      id: full, label: draft.name.trim(), kind: draft.bm25 && draft.dims === 0 ? "BM25-only" : "Knowledge base",
      metric: draft.metric, dims: draft.dims, profile: draft.profile,
      vectors: 0, deltas: 0, tombstones: 0, sizeBytes: 0, updated: DASH,
      searches: 0, p95: 0, cacheHit: 0, errors: 0, bm25: draft.bm25,
      status: "idle", baseVer: 0,
    };
  },

  /**
   * Sets the namespace's display LABEL. The key is the blob prefix and the unit
   * of authorization, so it cannot move — the modal copy says so too.
   */
  renameNamespace: async (id: string, name: string): Promise<void> => {
    await request(nsPath(id), {
      method: "PATCH",
      body: JSON.stringify({ display_name: name }),
    });
  },

  deleteNamespace: (id: string) => request<void>(nsPath(id), { method: "DELETE" }),

  compactNamespace: async (id: string): Promise<void> => {
    await request(nsPath(id, "/compact"), { method: "POST", body: "{}" });
  },

  // ---- documents --------------------------------------------------------
  listDocuments: async (namespace: string): Promise<DocumentRecord[]> => {
    const res = await request<ListDocumentsResponse>(nsPath(namespace, "/documents/list"), {
      method: "POST",
      body: JSON.stringify({ limit: 50, include_content: true, include_metadata: true }),
    });
    return (res.documents ?? []).map((d) => ({
      id: d.id,
      content: d.content ?? "",
      metadata: (d.metadata as Record<string, unknown>) ?? {},
    }));
  },

  upsertDocument: async (namespace: string, draft: DocumentDraft): Promise<UpsertAck> => {
    const metadata: Record<string, string> = {};
    for (const [k, v] of Object.entries(draft.metadata)) {
      if (k.trim()) metadata[k.trim()] = v;
    }
    // The server rejects an empty id; the form advertises it as optional, so
    // mint one here rather than surfacing a 400.
    const id = draft.id.trim() || `doc_${crypto.randomUUID().slice(0, 12)}`;
    // Refuse rather than quietly falling back to a server-side embed — the
    // caller asked for its own vector and would never learn it was dropped.
    if (draft.embed === "client" && !draft.vector?.length) {
      throw new ApiError(400, "client-side embedding needs a vector");
    }
    const res = await request<UpsertResponse>(nsPath(namespace, "/documents"), {
      method: "POST",
      body: JSON.stringify({
        documents: [
          {
            id,
            content: draft.content,
            metadata,
            ...(draft.embed === "client" ? { vector: draft.vector } : {}),
          },
        ],
      }),
    });
    return {
      operationId: res.operation_id ?? "",
      status: res.status ?? "queued",
    };
  },

  deleteDocument: async (namespace: string, id: string): Promise<void> => {
    await request(nsPath(namespace, "/documents/delete"), {
      method: "POST",
      body: JSON.stringify({ ids: [id] }),
    });
  },

  getOperation: async (namespace: string, operationId: string): Promise<Operation | null> => {
    try {
      const st = await request<RawOperation & { operation_id: string }>(
        nsPath(namespace, `/operations/${encodeURIComponent(operationId)}`),
      );
      return mapOperation({ ...st, id: st.operation_id, namespace });
    } catch (e) {
      if (e instanceof ApiError && (e.status === 404 || e.status === 503)) return null;
      throw e;
    }
  },

  // ---- query ------------------------------------------------------------
  query: async (input: QueryInput): Promise<QueryOutcome> => {
    // Hybrid fusion is a nested object on the wire (queryRequest.Hybrid in
    // core_handlers.go) — a flat `hybrid_bm25_text` is silently ignored, which
    // would make every "hybrid" query vector-only.
    const body = {
      query_text: input.text,
      top_k: input.topK,
      ...(input.hybrid ? { hybrid: { bm25_text: input.text } } : {}),
      include_content: true,
      include_metadata: true,
    };
    const res = await request<QueryResponse>(nsPath(input.namespace, "/query"), {
      method: "POST",
      body: JSON.stringify(body),
    });
    return {
      hits: hitsFrom(res.results),
      queryTimeMs: res.query_time_ms,
      request: body,
      response: res,
      responseStatus: "200 OK",
    };
  },

  multiQuery: async (input: MultiQueryInput): Promise<MultiQueryOutcome> => {
    const res = await request<MultiQueryResponse>("/v1/query", {
      method: "POST",
      body: JSON.stringify({
        namespaces: input.namespaces,
        query_text: input.text,
        top_k: input.topK,
        include_content: true,
        include_metadata: true,
      }),
    });
    return {
      hits: hitsFrom(res.results),
      queryTimeMs: res.query_time_ms,
      searchedNamespaces: res.searched_namespaces,
    };
  },

  // ---- pipeline ---------------------------------------------------------
  listOperations: async (): Promise<Operation[]> => {
    const res = await request<{ operations: RawOperation[] | null }>("/v1/operations");
    return (res.operations ?? []).map(mapOperation);
  },

  operationsSummary: async () => {
    const res = await request<{
      queued: number; in_flight: number; committed: number;
      dead_letter: number; backend: string;
    }>("/v1/operations/summary");
    return {
      inFlight: res.in_flight,
      queued: res.queued,
      deadLetter: res.dead_letter,
      backend: res.backend,
    };
  },

  listJobs: async (): Promise<Job[]> => {
    const res = await request<{ jobs: RawJob[] | null }>("/v1/code/admin/jobs");
    return (res.jobs ?? []).map(mapJob);
  },

  cancelJob: async (id: string): Promise<void> => {
    await request(`/v1/code/admin/jobs/${encodeURIComponent(id)}/cancel`, {
      method: "POST",
      body: "{}",
    });
  },

  compactionQueue: async (): Promise<CompactionEntry[]> => {
    const res = await request<{
      entries: { namespace: string; pressure: number; delta_segments: number; status: string }[] | null;
    }>("/v1/compaction/queue");
    return (res.entries ?? []).map((e) => ({
      namespace: e.namespace,
      // The server reports raw delta-over-base pressure, which can exceed 1;
      // the bar is a fraction, so clamp for display without hiding the value.
      pressure: Math.min(1, Math.max(0, e.pressure)),
      deltas: e.delta_segments,
      status: (e.status === "running" || e.status === "pending" ? e.status : "ready") as CompactionEntry["status"],
    }));
  },

  listTrash: async (): Promise<TrashEntry[]> => {
    const res = await request<{
      pending_deletions: { namespace: string; expires_at?: string; size_bytes?: number }[] | null;
    }>("/v1/pending-deletions");
    return (res.pending_deletions ?? []).map((e) => ({
      namespace: e.namespace,
      size: bytes(e.size_bytes ?? 0),
      expires: e.expires_at ? shortDate(e.expires_at) : DASH,
    }));
  },

  restoreTrash: async (namespace: string): Promise<void> => {
    await request("/v1/pending-deletions/restore", {
      method: "POST",
      body: JSON.stringify({ namespace }),
    });
  },

  purgeTrash: async (namespace: string): Promise<void> => {
    await request("/v1/pending-deletions/purge", {
      method: "POST",
      body: JSON.stringify({ namespace }),
    });
  },

  // ---- code search ------------------------------------------------------
  listRepos: async (): Promise<Repo[]> => {
    const res = await request<{ repos: RawRepo[] | null }>("/v1/code/admin/repos");
    return (res.repos ?? []).map(mapRepo);
  },

  connectRepo: async (_workspaceId: string, draft: RepoDraft): Promise<Repo> => {
    // The handler answers { repo, job_id }, not a bare repo.
    const res = await request<{ repo: RawRepo; job_id?: string }>(
      "/v1/code/admin/repos/connect",
      {
        method: "POST",
        body: JSON.stringify({
          full_name: draft.fullName,
          default_branch: draft.defaultBranch,
          register_webhook: draft.webhook,
        }),
      },
    );
    return mapRepo(res.repo);
  },

  // Both endpoints key on the connected namespace ("{repo}:{branch}"), not the
  // repo key — passing the repo key alone 404s.
  reindexRepo: async (namespace: string): Promise<void> => {
    await request("/v1/code/admin/reindex", {
      method: "POST",
      body: JSON.stringify({ namespace }),
    });
  },

  repairWebhook: async (namespace: string): Promise<void> => {
    await request("/v1/code/admin/webhook/repair", {
      method: "POST",
      body: JSON.stringify({ namespace }),
    });
  },

  /** Keys on the connected namespace, and sets the display label only. */
  renameRepo: async (namespace: string, name: string): Promise<void> => {
    await request(`/v1/code/admin/repos/${namespace.split("/").map(encodeURIComponent).join("/")}`, {
      method: "PATCH",
      body: JSON.stringify({ display_name: name }),
    });
  },

  // ---- access -----------------------------------------------------------
  listKeys: async (workspaceId: string): Promise<ApiKey[]> => {
    const res = await request<{ keys: RawKey[] | null }>(
      `/v1/workspaces/${encodeURIComponent(workspaceId)}/keys`,
    );
    return (res.keys ?? [])
      .filter((k) => !k.revoked)
      .map((k) => mapKey(k, workspaceId));
  },

  createKey: async (workspaceId: string, draft: KeyDraft) => {
    const res = await request<RawKey & { key?: string }>(
      `/v1/workspaces/${encodeURIComponent(workspaceId)}/keys`,
      {
        method: "POST",
        body: JSON.stringify({
          name: draft.name,
          access: draft.access,
          ...(draft.collections.length > 0 ? { collections: draft.collections } : {}),
        }),
      },
    );
    return { key: mapKey(res, workspaceId), secret: res.key ?? "" };
  },

  rotateKey: async (workspaceId: string, id: string) => {
    const res = await request<RawKey & { key?: string }>(
      `/v1/workspaces/${encodeURIComponent(workspaceId)}/keys/${encodeURIComponent(id)}/rotate`,
      { method: "POST", body: "{}" },
    );
    return { key: mapKey(res, workspaceId), secret: res.key ?? "" };
  },

  revokeKey: async (workspaceId: string, id: string): Promise<void> => {
    await request(
      `/v1/workspaces/${encodeURIComponent(workspaceId)}/keys/${encodeURIComponent(id)}`,
      { method: "DELETE" },
    );
  },

  renameKey: async (workspaceId: string, id: string, name: string): Promise<void> => {
    await request(
      `/v1/workspaces/${encodeURIComponent(workspaceId)}/keys/${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify({ name }) },
    );
  },

  listProfiles: async (): Promise<EmbedderProfile[]> => {
    const res = await request<{ profiles: RawProfile[] | null }>("/v1/embedder-profiles");
    return (res.profiles ?? []).map((p) => ({
      name: p.name,
      provider: p.provider || DASH,
      dims: p.dimensions,
      // An empty prefix is a real answer for most models — it means this
      // profile needs none — so it renders as "none", not as unknown.
      queryPrefix: p.query_prefix || "none",
      documentPrefix: p.document_prefix || "none",
      isDefault: p.is_default,
    }));
  },

  // ---- observability ----------------------------------------------------
  dashboardHealth: async (): Promise<HealthTile[]> => {
    // Two calls: the probe is public and cheap, the detail is authenticated
    // (it reports instance identity and backend topology). A detail failure
    // degrades the tiles rather than failing the dashboard.
    const [h, d] = await Promise.all([
      request<RawHealth>("/v1/health"),
      request<RawHealthDetail>("/v1/health/detail").catch(() => null),
    ]);
    const tiles: HealthTile[] = [
      {
        key: "api",
        value: h.status === "ok" ? "healthy" : h.status,
        tone: h.status === "ok" ? "ok" : "bad",
        detail: `${d?.instance ?? "instance"} · up ${humanAge(h.uptime_seconds)}`,
      },
      {
        key: "ingest",
        value: d?.ingest_owner ? "draining" : "idle",
        tone: d?.ingest_owner ? "ok" : "sec",
        detail: `backend ${d?.ingest_backend ?? DASH}`,
      },
      {
        key: "cache",
        value: h.cache === "connected"
          ? (d?.cache_hit_rate != null ? `${Math.round(d.cache_hit_rate * 100)}% hit` : "connected")
          : h.cache,
        tone: h.cache === "connected" ? "ok" : h.cache === "disabled" ? "sec" : "bad",
        detail: d?.cache_shared === false
          ? "process-local — figures cover this instance only"
          : `${h.namespaces_cached} namespaces cached`,
      },
      {
        key: "store",
        value: h.store,
        tone: h.store === "connected" ? "ok" : "bad",
        detail: d?.embedder?.profile ? `embedder ${d.embedder.profile}` : "blob storage",
      },
    ];
    return tiles;
  },

  analytics: async (): Promise<Analytics> => {
    const a = await request<RawAnalytics>("/v1/analytics");
    return {
      searchesTotal: a.searches_total,
      p50Ms: a.searches_total > 0 ? a.p50_ms : null,
      p95Ms: a.searches_total > 0 ? a.p95_ms : null,
      errorRate: a.searches_total > 0 ? a.error_rate : null,
      searchesSeries: a.series?.searches ?? [],
      p95Series: a.series?.p95_ms ?? [],
      cache: a.cache ?? { l1: 0, l2: 0, miss: 0 },
      namespaces: (a.namespaces ?? []).map((n) => ({
        id: n.namespace,
        label: n.namespace.split("/").slice(1).join("/") || n.namespace,
        searches: n.searches,
        p95: n.p95_ms,
        cacheHit: n.cache_hit,
        errors: n.errors,
      })),
    };
  },

  /**
   * Real meters and a real plan. There is no payment provider, so the amount,
   * card and invoice history are absent rather than invented — the screen shows
   * them as unavailable. See ROADMAP 3.
   */
  billing: async (workspaceId: string): Promise<Billing> => {
    const b = await request<RawBilling>(
      `/v1/workspaces/${encodeURIComponent(workspaceId)}/billing`,
    );
    // Billing units are DECIMAL (spec/11: GB = 1e9) — the shared bytes()
    // helper is binary (KiB-style) and would show one billed GB as 954 MB.
    const decBytes = (n: number): string => {
      if (n >= 1e12) return `${(n / 1e12).toFixed(2)} TB`;
      if (n >= 1e9) return `${(n / 1e9).toFixed(2)} GB`;
      if (n >= 1e6) return `${(n / 1e6).toFixed(1)} MB`;
      if (n >= 1e3) return `${(n / 1e3).toFixed(1)} KB`;
      return `${n} B`;
    };
    const qty = (used: number, unit: string): string => {
      switch (unit) {
        case "bytes":
          return decBytes(used);
        case "byte-hours":
          // Display GB-month equivalents: byte-hours / (1e9 * 730).
          return `${(used / (1e9 * 730)).toFixed(used > 0 && used < 73e10 ? 3 : 1)} GB-mo`;
        default:
          return compact(used);
      }
    };
    const usd = (micro?: number): string =>
      micro && micro > 0 ? `$${(micro / 1_000_000).toFixed(2)}` : "";
    const sub = b.subscription ?? undefined;
    return {
      plan: b.plan,
      blurb: planBlurb(b.plan),
      // An ESTIMATE from the same raw quantities the Stripe rollup drains —
      // the invoice is authoritative, and the screen labels it "est.".
      estimate: b.estimate_micro_dollars > 0 ? usd(b.estimate_micro_dollars) : DASH,
      nextDate: sub?.current_period_end ? sub.current_period_end.slice(0, 10) : DASH,
      card: sub?.card_brand && sub.card_last4 ? `${cap1(sub.card_brand)} ···· ${sub.card_last4}` : DASH,
      exp: DASH, // the provider does not expose card expiry through this seam
      billingEmail: sub?.billing_email || DASH,
      meters: (b.meters ?? []).map((m) => ({
        label: m.label,
        used: qty(m.used, m.unit),
        rate: m.rate ?? "",
        amount: usd(m.amount_micro_dollars),
        estimated: m.estimated,
      })),
      quotas: (b.quotas ?? []).map((q) => ({
        label: q.label,
        used: q.key === "storage_bytes" ? decBytes(q.used) : compact(q.used),
        cap: q.key === "storage_bytes" ? decBytes(q.limit) : compact(q.limit),
        pct: q.limit > 0 ? Math.min(1, q.used / q.limit) : 0,
      })),
      invoices: (b.invoices ?? []).map((inv) => ({
        period: inv.period_start.slice(0, 7),
        amount: `$${(inv.amount_due_cents / 100).toFixed(2)}`,
        status: inv.status,
      })),
    };
  },

  // ---- admin ------------------------------------------------------------
  listAllWorkspaces: async (): Promise<WorkspaceSummary[]> => {
    const res = await request<{ workspaces: RawSuperWorkspace[] | null }>(
      "/v1/superadmin/workspaces",
    );
    return (res.workspaces ?? []).map((w) => ({
      id: w.name || w.id,
      members: (w.members ?? []).length,
      namespaces: w.namespaces ?? 0,
      vectors: compact(w.vectors ?? 0),
      // Null means metering is unavailable, which is not the same as zero.
      searches: w.searches_24h == null ? DASH : compact(w.searches_24h),
      plan: w.plan ?? "free",
    }));
  },

  getSettings: async (workspaceId: string): Promise<ConsoleSettings> => {
    // Workspace settings and deployment configuration are two different things
    // with two different lifetimes; the screen shows them together, so both are
    // read here and the deployment half stays read-only.
    const [s, cfg] = await Promise.all([
      request<RawSettings>(`/v1/workspaces/${encodeURIComponent(workspaceId)}/settings`),
      request<RawConfig>("/v1/config").catch(() => null),
    ]);
    return {
      workspaceName: s.name,
      defaultEmbedder: s.default_embedder_profile || cfg?.default_embedder_profile || DASH,
      authEnabled: cfg?.auth_enabled ?? false,
      rateLimit: (cfg?.rate_limit ?? "disabled") !== "disabled",
      codeSearch: cfg?.code_search ?? false,
      corsAllowlist: (cfg?.cors_allowlist ?? []).join(", ") || DASH,
      ingestBackend: cfg?.ingest_backend ?? DASH,
      authChain: (cfg?.auth_chain ?? []).join(" → ") || DASH,
    };
  },

  saveSettings: async (workspaceId: string, patch: Partial<ConsoleSettings>): Promise<ConsoleSettings> => {
    // Only the workspace-owned fields are writable. The rest are process
    // configuration served read-only by GET /v1/config — sending them would be
    // a switch that does nothing.
    const body: Record<string, string> = {};
    if (patch.workspaceName !== undefined) body.name = patch.workspaceName;
    if (patch.defaultEmbedder !== undefined && patch.defaultEmbedder !== DASH) {
      body.default_embedder_profile = patch.defaultEmbedder;
    }
    if (Object.keys(body).length > 0) {
      await request(`/v1/workspaces/${encodeURIComponent(workspaceId)}/settings`, {
        method: "PATCH",
        body: JSON.stringify(body),
      });
    }
    return live.getSettings(workspaceId);
  },
};

// ------------------------------------------------------- code-admin shapes --

interface RawJob {
  id: string; kind?: string; namespace?: string; status?: string;
  started_at?: string; duration?: string; note?: string; error?: string;
}
/** repoView in internal/server/admin_handlers.go (registry.Repo + rollups). */
interface RawRepo {
  namespace?: string;
  repo?: string;
  owner?: string;
  name?: string;
  display_name?: string;
  default_branch?: string;
  branches?: string[];
  webhook_id?: number;
  webhook_status?: string;
  vector_count?: number;
  last_indexed_at?: string;
  status?: string;
  last_error?: string;
}
/** keyView in workspace_keys.go. */
interface RawKey {
  key_id: string;
  name?: string;
  access?: "read" | "read_write";
  collections?: string[];
  revoked?: boolean;
  created_at?: string;
  rotated_at?: string;
  calls_30d?: number;
  last_used_at?: string;
}
/** memberView in workspace_console.go. */
interface RawMember {
  uid: string;
  email?: string;
  role: string;
  created_at?: string;
}
/** embedderProfileEntry in core_handlers.go. */
interface RawProfile {
  name: string;
  provider?: string;
  model?: string;
  dimensions: number;
  query_prefix?: string;
  document_prefix?: string;
  is_default?: boolean;
  used_by?: string[];
}
/** superAdminWorkspaceView. */
interface RawSuperWorkspace {
  id: string;
  name: string;
  plan?: string;
  members?: unknown[];
  namespaces?: number;
  vectors?: number;
  searches_24h?: number | null;
}

function planBlurb(plan: string): string {
  switch (plan) {
    case "serverless":
      return "$25/mo + usage · $0 idle namespaces · no per-namespace minimum";
    case "vpc":
      return "$1,900/mo + 50% usage rates · data plane in your cloud";
    case "onprem":
      return "annual contract · customer-run · full feature parity";
    default:
      return "5 GB · 3 namespaces · 1M queries/mo · 1M writes/mo — free";
  }
}

/** "visa" → "Visa". */
function cap1(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

/** "dana@acme.ai" → "Dana"; the API stores no display name for members. */
function nameFromEmail(email: string): string {
  const local = email.split("@")[0] ?? "";
  if (!local) return email;
  return local.charAt(0).toUpperCase() + local.slice(1);
}

function initialsOf(email: string): string {
  const local = email.split("@")[0] ?? "";
  return (local.slice(0, 2) || "??").toUpperCase();
}

/** An email-only invite has no real uid; the console labels it as pending. */
function mapMember(m: RawMember): Member {
  const email = m.email ?? "";
  const pending = m.uid.startsWith("pending:");
  return {
    uid: m.uid,
    name: pending ? `${nameFromEmail(email)} (invited)` : nameFromEmail(email),
    email,
    role: m.role,
    // The server records no last-active timestamp; joined-at is what exists.
    lastActive: m.created_at ? shortDate(m.created_at) : DASH,
    initials: initialsOf(email),
  };
}

function mapJob(j: RawJob): Job {
  return {
    id: j.id,
    kind: (j.kind ?? "reindex") as Job["kind"],
    namespace: j.namespace ?? DASH,
    status: (j.status ?? "queued") as Job["status"],
    started: j.started_at ?? DASH,
    duration: j.duration ?? DASH,
    note: j.note ?? "",
    error: j.error,
  };
}

function mapRepo(r: RawRepo): Repo {
  const id = r.repo ?? r.namespace?.split(":")[0] ?? "";
  const owner = r.owner ?? id.split("/")[1] ?? "";
  const name = r.name ?? id.split("/").pop() ?? id;
  const branch = r.default_branch ?? "main";
  return {
    id,
    namespace: r.namespace ?? `${id}:${branch}`,
    name: r.display_name || (owner ? `${owner}/${name}` : name),
    defaultBranch: branch,
    branches: r.branches ?? [branch],
    // The list reports whether a hook is registered, not whether GitHub still
    // delivers to it — a real verdict costs an API call per repo and lives on
    // the per-repo webhook endpoint.
    webhook: (r.webhook_status ?? (r.webhook_id ? "configured" : "none")) === "configured"
      ? "healthy"
      : "stale",
    vectors: r.vector_count ?? 0,
    lastIndexed: r.last_indexed_at ? shortDate(r.last_indexed_at) : DASH,
    status: (r.status ?? "ready") as Repo["status"],
  };
}

/** The console's scope chips are the display form of the wire `access` value. */
export function scopesForAccess(access: RawKey["access"]): string[] {
  return access === "read_write" ? ["docs:read", "docs:write"] : ["docs:read"];
}

function mapKey(k: RawKey, workspaceId: string): ApiKey {
  const collections = k.collections ?? ["*"];
  return {
    id: k.key_id,
    name: k.name || k.key_id,
    // The secret is returned exactly once at creation, so a listed key can only
    // ever be shown by id.
    masked: `vf_live_••••••••${k.key_id.slice(-4)}`,
    scopes: scopesForAccess(k.access),
    grants: collections.map((c) => `${workspaceId}/${c}`).join(", ") || DASH,
    created: k.created_at ? shortDate(k.created_at) : DASH,
    // Metered by the auth middleware. Zero is a real answer (an unused key);
    // the field is only a dash when the server omitted it entirely.
    calls: k.calls_30d == null ? DASH : compact(k.calls_30d),
  };
}

// ----------------------------------------------------------- the export ----

/**
 * With the fallback disabled, the STATIC_BACKED methods reject instead of
 * quietly serving fixtures — the screens render their "not wired yet" state.
 */
function withoutFallback(base: DataSource): DataSource {
  const guarded = { ...base } as unknown as Record<string, unknown>;
  for (const name of STATIC_BACKED) {
    guarded[name] = () =>
      Promise.reject(
        new ApiError(501, `${name} has no endpoint yet — see ROADMAP.md`),
      );
  }
  return guarded as unknown as DataSource;
}

/** Wraps the mutating fallbacks so a local-only change says so. */
function announcingWrites(base: DataSource): DataSource {
  if (STATIC_WRITES.size === 0) return base;
  const wrapped = { ...base } as unknown as Record<string, unknown>;
  for (const name of STATIC_WRITES) {
    const original = base[name] as (...args: unknown[]) => Promise<unknown>;
    wrapped[name] = async (...args: unknown[]) => {
      const result = await original(...args);
      announceFakeWrite(name);
      return result;
    };
  }
  return wrapped as unknown as DataSource;
}

// staticSource is still imported: it backs the /dev mount, and withoutFallback
// keeps working if a future method loses its endpoint.
void staticSource;

export const httpSource: DataSource = STATIC_FALLBACK
  ? announcingWrites(live)
  : withoutFallback(live);
